import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, fieldErrors, unwrap } from '../../api/client';
import type { Employee } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { AssetLink } from '../../components/assets/AssetLink';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  DataList,
  Dialog,
  EmptyState,
  ErrorBox,
  Field,
  FormGrid,
  Input,
  Loading,
  nullIfEmpty,
  PageHeader,
  Pagination,
  StatusBadge,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
} from '../../components/ui';
import { formatDate } from '../../lib/format';

export function EmployeeListPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const pageSize = 50;

  const list = useQuery({
    queryKey: ['employees', 'list', { q, showInactive, page }],
    queryFn: () => unwrap(api.GET('/api/employees', { params: { query: { q: q || undefined, active: showInactive ? undefined : 'true', page, page_size: pageSize } } })),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Mitarbeiter"
        actions={
          can('masterdata:write') && (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Mitarbeiter anlegen
            </Button>
          )
        }
      />
      <Card className="mb-4 flex flex-wrap items-center gap-4 p-3">
        <form
          className="relative min-w-60 flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(term.trim());
            setPage(1);
          }}
        >
          <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-slate-400" />
          <Input className="pl-8" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Kürzel, Name, E-Mail, Abteilung …" aria-label="Suche" />
        </form>
        <Checkbox label="Inaktive anzeigen" checked={showInactive} onChange={(e) => (setShowInactive(e.target.checked), setPage(1))} />
      </Card>
      <Card>
        {list.isPending ? (
          <Loading />
        ) : list.error ? (
          <ErrorBox error={list.error} className="m-4" />
        ) : list.data.data.length === 0 ? (
          <EmptyState title="Keine Mitarbeiter gefunden" />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Kürzel</Th>
                  <Th>Name</Th>
                  <Th>Abteilung</Th>
                  <Th>E-Mail</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((e) => (
                  <Tr key={e.id} onClick={() => navigate(`/employees/${encodeURIComponent(e.short_code)}`)}>
                    <Td className="font-mono font-medium text-brand-700">{e.short_code}</Td>
                    <Td className="font-medium text-slate-900">{e.name}</Td>
                    <Td>{e.department ?? '–'}</Td>
                    <Td>{e.email ?? '–'}</Td>
                    <Td>{e.is_active ? <Badge tone="green">Aktiv</Badge> : <Badge>Inaktiv</Badge>}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={pageSize} total={list.data.total} onPage={setPage} />
          </>
        )}
      </Card>
      <EmployeeDialog employee={null} open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

export function EmployeeDetailPage() {
  const { ref = '' } = useParams();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const emp = useQuery({ queryKey: ['employees', 'detail', ref], queryFn: () => unwrap(api.GET('/api/employees/{ref}', { params: { path: { ref } } })) });
  const assets = useQuery({ queryKey: ['employees', 'assets', ref], queryFn: () => unwrap(api.GET('/api/employees/{ref}/assets', { params: { path: { ref } } })) });

  if (emp.isPending) return <Loading />;
  if (emp.error) return <ErrorBox error={emp.error} />;
  const e = emp.data;

  return (
    <>
      <PageHeader
        back={
          <Link to="/employees" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Mitarbeiter
          </Link>
        }
        title={
          <span>
            <span className="font-mono">{e.short_code}</span> – {e.name}
          </span>
        }
        subtitle={e.is_active ? <Badge tone="green">Aktiv</Badge> : <Badge>Inaktiv</Badge>}
        actions={
          can('masterdata:write') && (
            <Button icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
              Bearbeiten
            </Button>
          )
        }
      />
      <div className="grid gap-5 lg:grid-cols-3">
        <Card>
          <CardHeader title="Daten" />
          <CardBody>
            <DataList
              className="sm:grid-cols-1"
              items={[
                ['E-Mail', e.email],
                ['Abteilung', e.department],
                ['Aktuelle Geräte', e.current_asset_count],
                !!e.notes && ['Notizen', <span className="whitespace-pre-line">{e.notes}</span>],
              ]}
            />
          </CardBody>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader title="Aktuell zugeordnet" />
          {assets.isPending ? (
            <Loading />
          ) : assets.error ? (
            <ErrorBox error={assets.error} className="m-4" />
          ) : assets.data.current.length === 0 ? (
            <EmptyState title="Keine Geräte zugeordnet" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Nr.</Th>
                  <Th>Bezeichnung</Th>
                  <Th>Typ</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {assets.data.current.map((a) => (
                  <Tr key={a.id}>
                    <Td>
                      <AssetLink asset={a} />
                    </Td>
                    <Td>
                      {a.name}
                      {a.parent && <span className="block text-xs text-slate-500">in {a.parent.asset_number}</span>}
                    </Td>
                    <Td>{a.asset_type.label}</Td>
                    <Td>
                      <StatusBadge status={a.status} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        <Card className="lg:col-span-3">
          <CardHeader title="Verlauf der Zuordnungen" />
          {assets.data && assets.data.history.length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <Th>Asset</Th>
                  <Th>Von</Th>
                  <Th>Bis</Th>
                  <Th>Notiz</Th>
                </tr>
              </thead>
              <tbody>
                {assets.data.history.map((p) => (
                  <Tr key={p.id}>
                    <Td>
                      <AssetLink asset={p.asset} /> {p.asset.name}
                    </Td>
                    <Td>{formatDate(p.valid_from)}</Td>
                    <Td>{p.is_current ? <Badge tone="green">aktuell</Badge> : formatDate(p.valid_to)}</Td>
                    <Td className="text-xs text-slate-500">{[p.start_note, p.end_note].filter(Boolean).join(' / ') || '–'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          ) : (
            !assets.isPending && <EmptyState title="Noch keine Zuordnungen" />
          )}
        </Card>
      </div>
      <EmployeeDialog employee={e} open={editing} onClose={() => setEditing(false)} />
    </>
  );
}

function EmployeeDialog({ employee, open, onClose }: { employee: Employee | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState({ short_code: '', name: '', email: '', department: '', notes: '', is_active: true });
  useEffect(() => {
    if (open)
      setF({
        short_code: employee?.short_code ?? '',
        name: employee?.name ?? '',
        email: employee?.email ?? '',
        department: employee?.department ?? '',
        notes: employee?.notes ?? '',
        is_active: employee?.is_active ?? true,
      });
  }, [open, employee]);

  const m = useMutation({
    mutationFn: () => {
      const body = { short_code: f.short_code.trim(), name: f.name.trim(), email: nullIfEmpty(f.email), department: nullIfEmpty(f.department), notes: nullIfEmpty(f.notes) };
      return employee
        ? unwrap(api.PATCH('/api/employees/{ref}', { params: { path: { ref: employee.id } }, body: { ...body, is_active: f.is_active } }))
        : unwrap(api.POST('/api/employees', { body }));
    },
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ['employees'] });
      toast(employee ? 'Gespeichert' : `${e.short_code} angelegt`);
      onClose();
      if (!employee || employee.short_code !== e.short_code) navigate(`/employees/${encodeURIComponent(e.short_code)}`, { replace: !!employee });
    },
  });
  const errs = fieldErrors(m.error);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={employee ? 'Mitarbeiter bearbeiten' : 'Mitarbeiter anlegen'}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!f.short_code.trim() || !f.name.trim()}>
            Speichern
          </Button>
        </>
      }
    >
      <FormGrid>
        <Field label="Kürzel" required error={errs.short_code} hint="z. B. MK">
          {(p) => <Input {...p} value={f.short_code} onChange={(e) => setF({ ...f, short_code: e.target.value })} />}
        </Field>
        <Field label="Name" required error={errs.name}>
          {(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
        </Field>
        <Field label="E-Mail" error={errs.email}>
          {(p) => <Input {...p} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />}
        </Field>
        <Field label="Abteilung" error={errs.department}>
          {(p) => <Input {...p} value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} />}
        </Field>
        <Field label="Notizen" className="sm:col-span-2">
          {(p) => <Textarea {...p} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />}
        </Field>
      </FormGrid>
      {employee && (
        <Checkbox label="Aktiv (inaktive Mitarbeiter können keine Geräte mehr erhalten)" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} />
      )}
      <ErrorBox error={m.error} />
    </Dialog>
  );
}
